import { useState } from 'react';
import CloseIcon from '../../assets/icons/close.svg?react';
import { cn } from '../../utils/className';
import { TableMultiSelectAction } from './types';

interface TableMultiSelectActionsProps {
  selectedCount: number;
  actions?: TableMultiSelectAction[];
  className?: string;
  onClearSelection?: () => void;
}

export function TableMultiSelectActions({
  selectedCount,
  actions = [],
  className,
  onClearSelection,
}: TableMultiSelectActionsProps) {
  const [shouldRender, setShouldRender] = useState(false);
  const [isHoveringCount, setIsHoveringCount] = useState(false);

  const hasSelection = selectedCount > 0;
  const isVisible = hasSelection;

  // Keep component mounted if opening or if we need to animate out
  const show = hasSelection || shouldRender;

  const handleTransitionEnd = () => {
    if (!hasSelection) {
      setShouldRender(false);
      setIsHoveringCount(false);
    }
  };

  // Show immediately when opening
  if (hasSelection && !shouldRender) {
    setShouldRender(true);
  }

  if (!show) return null;

  return (
    <div
      className={cn(
        'flex h-10 w-fit items-center gap-8',
        'transition-all duration-200 ease-out',
        isVisible ? 'opacity-100' : 'opacity-0',
        className
      )}
      onTransitionEnd={handleTransitionEnd}
    >
      <div
        className={cn(
          'flex items-center gap-2.5',
          'transition-all duration-200',
          onClearSelection && 'cursor-pointer hover:opacity-80'
        )}
        onMouseEnter={() => setIsHoveringCount(true)}
        onMouseLeave={() => setIsHoveringCount(false)}
        onClick={() => {
          if (onClearSelection) {
            onClearSelection();
          }
        }}
        role={onClearSelection ? 'button' : undefined}
        aria-label={onClearSelection ? 'Clear selection' : undefined}
      >
        <div
          className='flex h-6 w-6 shrink-0 items-center justify-center
            rounded-full'
          style={{
            backgroundColor: 'var(--color-blue-100)',
            color: 'var(--color-focus)',
          }}
        >
          {isHoveringCount && onClearSelection ? (
            <CloseIcon className='h-3 w-3' />
          ) : (
            <span className='size-p2 font-regular leading-none'>
              {selectedCount}
            </span>
          )}
        </div>
        <span className='size-p2 text-primary font-regular'>Selected</span>
      </div>

      <div className='flex items-center gap-8'>
        {actions.map((action, index) => (
          <div key={index} className='flex items-center'>
            {action.variant === 'primary' ? (
              <button
                onClick={action.onClick}
                className={cn(
                  'flex items-center gap-1.5 rounded-lg px-3 py-1.5',
                  'bg-focus hover:bg-focus-hover text-white',
                  'cursor-pointer transition-colors',
                  `focus-visible:ring-focus focus-visible:ring-1
                    focus-visible:outline-none`,
                  '[&_path]:fill-current [&_svg]:fill-current'
                )}
              >
                {action.icon && (
                  <span
                    className='flex h-4 w-4 shrink-0 items-center
                      justify-center'
                  >
                    {action.icon}
                  </span>
                )}
                <span className='size-p2 font-medium'>{action.label}</span>
              </button>
            ) : (
              <button
                onClick={action.onClick}
                className={cn(
                  'text-primary flex items-center gap-2 rounded-lg',
                  'transition-colors',
                  'focus-visible:text-secondary hover:opacity-80',
                  'focus-visible:outline-none',
                  'cursor-pointer',
                  '[&_path]:fill-current [&_svg]:fill-current'
                )}
              >
                {action.icon && (
                  <span
                    className='flex h-4 w-4 shrink-0 items-center
                      justify-center'
                  >
                    {action.icon}
                  </span>
                )}
                <span className='size-p2 font-regular'>{action.label}</span>
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
